import React, { useEffect, useState } from "react";
import { MacoMatch } from "../../typings";
import {getMacoMatches, searchFamily, searchSpecies} from "../../firebase";
import {generatePath, createSearchParams, Link, useSearchParams} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { motion } from "motion/react";
import {MACO_MATCHES_DETAILS_ROUTE, MACO_PEAKS_DETAILS_ROUTE} from "../../Routes";
import {useMacoSpectrum} from "../../hooks/useMacoSpectrum";
import {filterHIPeaks} from "../../utils/filter-hi-peaks";
import {useHasRole} from "../../hooks/useHasRole";
import {getShiftedSpectra} from "../../utils/get-shifted-spectra";
import {AsyncMultiSelector} from "../../components/selectors/AsyncMultiSelector";


export const MacoMatches: React.FC = () => {
  const { spectra, isLoading: isSpectraLoadig, error: linkError, fromLink, hash } = useMacoSpectrum();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [matchData, setMatchData] = useState<MacoMatch[]>([]);
  const [minMatches, setMinMatches] = useState<number>(10);
  const [numberHIPeaks, setNumberHIPeaks] = useState<number>(50);
  const [getHIPeaks, setGetHIPeaks] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(40);
  const [ppm, setPpm] = useState<number>(800);
  const [shift, setShift] = useState<number>(0);
  const [min, setMin] = useState<number>(3000);
  const [max, setMax] = useState<number>(20000);
  const [useCalibratedMasses, setUseCalibratedMasses] = useState<boolean>(false);
  const [database, setDatabase] = useState<string>("default");
  const [familyQuery, setFamilyQuery] = useState<string[]>([]);
  const [speciesQuery, setSpeciesQuery] = useState<string[]>([]);
  const [isMatchDataLoading, setIsMatchDataLoading] = useState<boolean>(false);
  // Only the profiles the automatic identification matched. A way to look at
  // what the classifier based its answer on, among everything else MaCo finds.
  const [onlyMatchedProfiles, setOnlyMatchedProfiles] = useState<boolean>(false);
  const { hasRole: hasFungiRole } = useHasRole("fungi");
  const { hasRole: hasStagingRole } = useHasRole("staging");
  const [ searchParams ] = useSearchParams();

  // on first render if database param is "Fungi-SSP" set database to "Fungi-compare"
  useEffect(() => {
    if(!searchParams.has("database")) return;
    const database = searchParams.get("database") as string;
    if(database === "Fungi-SSP") setDatabase("Fungi-compare");
  }, [searchParams]);

  // Settings that follow from the spectrum, whichever way it arrived. Recorded
  // as applied, so the first comparison waits for them: run before, it would
  // use the defaults, and if it came back last it would overwrite the right
  // answer with the wrong one.
  const [settingsAppliedTo, setSettingsAppliedTo] = useState<typeof spectra>(undefined);
  useEffect(() => {
    if (!spectra) return;
    const hasCalibratedMasses = !!spectra.calibratedMasses;
    setShift(spectra.shift || 0);
    if (spectra.database === "Fungi-SSP") setDatabase("Fungi-compare");
    else setDatabase(spectra.databaseUsed || "default");
    setUseCalibratedMasses(hasCalibratedMasses);
    if (hasCalibratedMasses) setPpm(300);
    setSettingsAppliedTo(spectra);
  }, [spectra]);

  // Compare once the spectrum's settings are in, and again whenever the
  // database, the calibration or the HI filter changes.
  useEffect(() => {
    if (!spectra || settingsAppliedTo !== spectra) return;
    handelGetMacoMatches().catch(console.error);
  }, [database, useCalibratedMasses, getHIPeaks, settingsAppliedTo]);

  const matchedProfiles = spectra && Array.isArray(spectra.matchedProfiles) ? spectra.matchedProfiles : [];
  // The matched profiles are profiles of the database the identification used.
  // In any other database they do not exist, and the filter would empty the
  // list — so it only applies, and can only be ticked, on that database.
  const matchedDatabase = spectra?.databaseUsed || spectra?.database;
  const canFilterMatched = matchedProfiles.length > 0 && database === matchedDatabase;
  const shownMatches = onlyMatchedProfiles && canFilterMatched
    ? matchData.filter(({ profile }) => matchedProfiles.includes(profile))
    : matchData;

  const getMatchDetailPath = (selectedProfile: string): string => {
    if(!spectra) return "";
    // A spectrum from a link lives only in the fragment, so the detail page
    // gets the fragment too.
    const path = fromLink
      ? generatePath(MACO_PEAKS_DETAILS_ROUTE, { selectedProfile: encodeURIComponent(selectedProfile) })
      : generatePath(MACO_MATCHES_DETAILS_ROUTE, { id: spectra.id, selectedProfile: encodeURIComponent(selectedProfile) });
    const params = {
      database: encodeURIComponent(database),
      ppm: ppm.toString(),
      hi: numberHIPeaks.toString(),
      getHI: getHIPeaks.toString(),
      shift: shift.toString(),
      min: min.toString(),
        max: max.toString(),
        useCalibratedMasses: useCalibratedMasses.toString(),
    }

    return `${path}?${createSearchParams(params)}${fromLink ? hash : ""}`;
  }

  const hasCalibratedMasses = spectra&& spectra.calibratedMasses && spectra.calibratedMasses.length > 0;

  // function get maco matches from cloud run
  const handelGetMacoMatches = async () => {
    if (!spectra) {
      console.error("No spectra loaded");
      setErrorMessage("No spectra loaded");
      return;
    }

    if (spectra.masses && spectra.masses.length === 0) {
      console.error("No masses in spectra");
      setErrorMessage("No masses in spectra");
      return;
    }

    setErrorMessage(undefined);
    setIsMatchDataLoading(true);
    setMatchData([]);



    let masses = Array.isArray(spectra.masses) ? spectra.masses: [];
    if(hasCalibratedMasses && useCalibratedMasses){
        masses = Array.isArray(spectra.calibratedMasses) ? spectra.calibratedMasses.map(x => ({mz: x.mz, int: x.intensity})) : [];
    }
    masses = masses.filter((peak) => peak.mz >= min && peak.mz <= max);
    if(getHIPeaks) masses = filterHIPeaks(masses, numberHIPeaks);


    try {
      const { data } = await getMacoMatches({
        minMatches: minMatches,
        limit: limit,
        database: database,
        ppm: ppm,
        familyQuery: familyQuery,
        speciesQuery: speciesQuery,
        masses: getShiftedSpectra(shift, masses),

      });
      setIsMatchDataLoading(false);

      if(data.length === 0) setErrorMessage("No matches found");
      setMatchData(data);
    } catch (error) {
      console.error("Failed to fetch maco matches", error);
      setErrorMessage("Failed to fetch matches");
      setIsMatchDataLoading(false);
    }
  };

  const getFamilyOptions = async (inputValue: string) => {
    try {
      const { data } = await searchFamily({ query: inputValue, limit: 10, database });
      return data.map((family: string) => ({ value: family, label: family }));
    } catch (error) {
      console.error("Error fetching family:", error);
      return [];
    }
  }

  const getSpeciesOptions = async (inputValue: string) => {
    try {
      const { data } = await searchSpecies({ query: inputValue, limit: 10, database });
      return data.map((species: string) => ({ value: species, label: species }));
    } catch (error) {
      console.error("Error fetching species:", error);
      return [];
    }
  }

  if (isSpectraLoadig) {
    return (
      <div className="container min-vh-100 align-items-center">
        <div
          className="position-absolute top-50 start-50 spinner-border text-primary"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (spectra === undefined) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12">
            <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />{" "}
            {linkError ?? "Spectra with not found."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-12 col-lg-6 mb-20 mb-lg-20">
          <div>
            <span>
              Filename: <strong>{spectra?.file}</strong>
            </span>
          </div>
          <div>
            <span>
              Sample: <strong>{spectra?.sample}</strong>
            </span>
          </div>
          <div>
            <span>
              Datacount:{" "}
              <strong>{spectra?.masses && spectra.masses.length}</strong>
            </span>
          </div>
        </div>

        <form onSubmit={async (e) => {
          e.preventDefault();
          await handelGetMacoMatches();
        }}>
          <div className="col-12">
            <div className="row mb-20">
              <div className="col-3">
                <div className="input-group">
                  <span className="input-group-text">Min Matches</span>
                  <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={minMatches}
                      onChange={(e) => setMinMatches(parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="col-2">
                <div className="input-group">
                  <span className="input-group-text">Limit</span>
                  <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={limit}
                      onChange={(e) => setLimit(parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="col-3">
                <div className="input-group">
                  <span className="input-group-text">PPM</span>
                  <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={ppm}
                      step={50}
                      onChange={(e) => setPpm(parseInt(e.target.value))}
                  />
                </div>
              </div>
              {/* The two checkboxes share one column, each as wide as its
                  label and the pair flush right. Two columns of their own were
                  too narrow below 1400 px: the box broke onto a line of its own. */}
              <div className="col-4 d-flex justify-content-end gap-20">
                <div className="input-group flex-nowrap w-auto">
                  <span className="input-group-text">Calibrated</span>
                  <div className="input-group-text">
                    <input
                        disabled={!hasCalibratedMasses}
                        type="checkbox"
                        checked={useCalibratedMasses}
                        onChange={(e) => setUseCalibratedMasses(e.target.checked)}
                    />
                  </div>
                </div>
                <div
                    className="input-group flex-nowrap w-auto"
                    title={canFilterMatched
                      ? "Only the profiles the automatic identification matched"
                      : matchedProfiles.length === 0
                        ? "The identification matched no profiles for this spectrum"
                        : `The matched profiles are in ${matchedDatabase}, not in the selected database`}
                >
                  <span className="input-group-text">Only matched</span>
                  <div className="input-group-text">
                    <input
                        disabled={!canFilterMatched}
                        type="checkbox"
                        checked={onlyMatchedProfiles && canFilterMatched}
                        onChange={(e) => setOnlyMatchedProfiles(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-20">
              <div className="col-4">
                <div className="input-group">
                  <span className="input-group-text">HI</span>
                  <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={numberHIPeaks}
                      step={10}
                      onChange={(e) => setNumberHIPeaks(parseInt(e.target.value))}
                  />
                  <div className="input-group-text">
                    <input
                        type="checkbox"
                        checked={getHIPeaks}
                        onChange={(e) => setGetHIPeaks(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              <div className="col-2">
                <div className="input-group">
                  <span className="input-group-text">Shift</span>
                  <input
                      type="number"
                      className="form-control"
                      value={shift}
                      step={100}
                      onChange={(e) => setShift(parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="col-3">
                <div className="input-group">
                  <span className="input-group-text">Min</span>
                  <input
                      type="number"
                      className="form-control"
                      min={3000}
                      max={max}
                      value={min}
                      step={500}
                      onChange={(e) => setMin(parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="col-3">
                <div className="input-group">
                  <span className="input-group-text">Max</span>
                  <input
                      type="number"
                      className="form-control"
                      min={min}
                      max={20000}
                      value={max}
                      step={500}
                      onChange={(e) => setMax(parseInt(e.target.value))}
                  />
                </div>
              </div>
            </div>
            <div className="row mb-20">
              <div className="col-6">
                <AsyncMultiSelector placeholder="Filter family" onChange={setFamilyQuery} getOptions={getFamilyOptions} />
              </div>
              <div className="col-6">
                <AsyncMultiSelector placeholder="Filter genus/species" onChange={setSpeciesQuery} getOptions={getSpeciesOptions} />
              </div>
            </div>
            <div className="row mb-20">
              <div className="col-12">
                <div className="input-group">
                  <span className="input-group-text">Database</span>
                  <select
                      className="form-select text-truncate"
                      id="database"
                      name="database"
                      aria-label="Select database"
                      value={database}
                      onChange={(e) => setDatabase(e.target.value)}
                  >
                    <option value="default">All Bacteria</option>
                    <option value="Acinetobacter-baumannii-complex">
                      Acinetobacter baumannii complex
                    </option>
                    <option value="Bacillus-cereus-group">
                      Bacillus cereus group
                    </option>
                    <option value="Bordetella-pertussis-parapertussis-bronchiseptica">
                      Bordetella pertussis / parapertussis / bronchiseptica
                    </option>
                    <option value="Burkholderia-cepacia-complex">
                      Burkholderia cepacia complex
                    </option>
                    <option value="Escherichia-Shigella">
                      Escherichia / Shigella
                    </option>
                    <option value="Enterobacter-cloacae-complex">
                      Enterobacter cloacae complex
                    </option>
                    <option value="Klebsiella-Raoultella">
                      Klebsiella / Raoultella
                    </option>
                    <option value="Listeria">
                      Listeria "sensu stricto" clade
                    </option>
                    <option value="Salmonella">Salmonella</option>
                    <option value="Staphylococcus-aureus-complex">
                      Staphylococcus aureus complex
                    </option>
                    <option value="Streptococcus-mitis-group">
                      Streptococcus mitis group
                    </option>
                    {hasFungiRole && (
                        <option value="Fungi-SSP">Fungi SSP</option>
                    )}
                    {hasFungiRole && (
                        <option value="Fungi-compare">Fungi Compare</option>
                    )}
                    {hasStagingRole && (
                        <option value="Staging-Default">Staging Default</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mb-20 d-flex gap-30">
            <button
                type="button"
                className="btn btn-outline-primary"
                onClick={window.close}>
              Close
            </button>
            <button
                type="submit"
                className="btn btn-primary"
                disabled={isMatchDataLoading}
            >
              {isMatchDataLoading ? (
                  <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                  />
              ) : (
                  "Compare"
              )}
            </button>
          </div>
        </form>

        <div className="col-12">
          <div className="d-none d-lg-block overview-results__header bg-primary py-20 px-30">
            <div className="row">
              <div className="col-4">
                <strong className="text-black">Profile</strong>
              </div>
              <div className="col-2">
                <strong className="text-black">Family</strong>
              </div>
              <div className="col-2">
                <strong className="text-black">Genus</strong>
              </div>
              <div className="col-3">
                <strong className="text-black">Species</strong>
              </div>
              <div className="col-1">
                <strong className="text-black">Matches</strong>
              </div>
            </div>
          </div>
        </div>


        {errorMessage && (<div className="col-12 text-center mt-10"><FontAwesomeIcon
            icon={faExclamationTriangle as IconProp}/> {errorMessage}</div>)}

        {onlyMatchedProfiles && canFilterMatched && matchData.length > 0 && shownMatches.length === 0 && (
            <div className="col-12 text-center mt-10">
              None of the matched profiles is among these results. Raise the limit or lower min matches.
            </div>
        )}

        {shownMatches.map(({profile, family, genus, species_plus, matches}, i) => (
            <motion.div
                initial={{
                  opacity: 0,
                  y: 10
                }}
                animate={{
                  opacity: 1,
                  y: 0
                }}
                transition={{duration: 0.1, delay: i * 0.03}}
                className="col-12" key={i}>
              <Link to={getMatchDetailPath(profile)} target="_blank">
                <div className="detail-results__wrapper bg-white">
                  <div className="row gy-20">
                    <div className="col-12 col-sm-6 col-lg-4">
                <span className="text-break">
                  <span className="d-block d-lg-none fw-bold">Profile:</span>
                  {profile}
                </span>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-2">
                <span className="text-break">
                  <span className="d-block d-lg-none fw-bold">Family</span>
                  {family}
                </span>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-2">
                <span className="text-break">
                  <span className="d-block d-lg-none fw-bold">Genus:</span>
                  {genus}
                </span>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-3">
                      <div>
                        <span className="d-block d-lg-none fw-bold">Species:</span>
                        <div className="fw-bold">{species_plus}</div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-1">
                      <div>
                        <span className="d-block d-lg-none fw-bold">Matches:</span>
                        <div className="fw-bold">{matches}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
        ))}
      </div>
    </div>
  );
};
